import CollapsibleSection from "components/Section";
import { Button } from "components/Button";
import React, { useEffect, useState } from "react";
import TextValue from "components/TitleValue";
import { useTranslation } from "react-i18next";
import {
  ApartmentType,
  TApartment,
  TWaterMeter,
  TWaterSubscriber,
  WaterMeterAuthType,
  WaterMeterCommunicationType,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { TransparentLoading } from "components/TransparentLoading";
import { Select } from "components/Select";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { toast } from "react-toastify";
import { useRegion_v2 } from "contexts/regions_v2";
import { MapLocationPicker_v2 } from "components/MapLocationPicker_v2";
import { Permission } from "components/Permission";
import { TUserRole } from "configs/routes";

type TProps = {
  data?: TApartment;
  isLoading: boolean;
  onSaveClick: (e: TEditFormTypes) => void;
  isButtonLoading: boolean;
  enableEditing?: boolean;
  onEditClick: () => void;
};
export type TEditFormTypes = {
  address: string;
  identifierId: string;
  numberOfFloor: number;
  location: { lat: number; lng: number } | null;
  // latitude: number;
  // longitude: number;
  jurisdictionId: string;
  apartmentType: ApartmentType;
};

const apartmentTypeOptions = SortObjectByPropName(
  Enum2Array(ApartmentType),
  "name"
);
export const ApartmentInfo = (props: TProps) => {
  const {
    data,
    isLoading,
    onSaveClick,
    isButtonLoading,
    enableEditing,
    onEditClick,
  } = props;

  const { t } = useTranslation();
  const { allJurisdictions } = useRegion_v2();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
    trigger,
  } = useForm<TEditFormTypes>({
    defaultValues: undefined,
  });

  const getJurisdictionData = () => {
    return allJurisdictions.find(
      (item) => item.id === getValues("jurisdictionId")
    );
  };

  useEffect(() => {
    if (data) {
      setValue("address", data?.address);
      setValue("identifierId", data?.identifierId);
      // setValue("latitude", data?.latitude);
      // setValue("longitude", data?.longitude);
      setValue("location", {
        lat: data?.latitude || 35.7219,
        lng: data?.longitude || 51.3347,
      });
      setValue("numberOfFloor", data?.numberOfFloor);
      setValue("jurisdictionId", data?.jurisdictionId);
      setValue("apartmentType", data?.apartmentType);
    }
  }, [data]);

  return (
    <CollapsibleSection
      title={t("apartmentInfo")}
      collapsible={false}
      isLoading={isLoading}
      skeleton="text"
    >
      <div className="w-full">
        <div className="flex flex-col w-full gap-y-3">
          <div className={"flex"}>
            <p>{t("province")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.provinceName}
            </p>
            <p className={"mr-1"}>{t("city")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.cityName}
            </p>
            <p className={"mr-1"}>{t("jurisdiction")}</p>
            <p className={"text-primary-60 font-bold mr-1"}>
              {getJurisdictionData()?.name}
            </p>
          </div>
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name={"identifierId"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("identifierId")}
                    placeholder={t("identifierId")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"apartmentType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="flex-1"
                    label={t("apartmentType")}
                    options={apartmentTypeOptions}
                    initialValue={apartmentTypeOptions.find(
                      (item) =>
                        (item.value as unknown as ApartmentType) === field.value
                    )}
                    isError={!!error}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"numberOfFloor"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("numberOfFloor")}
                    type={"number"}
                    placeholder={t("numberOfFloor")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
          </div>
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <div className={"w-full h-[360px] flex-grow"}>
                  <MapLocationPicker_v2
                    className={"w-full h-full"}
                    onLocationPick={(coordinate) => {
                      field.onChange(coordinate);
                    }}
                    initialMarker={{
                      lat: watch("location")?.lat || 35.7219,
                      lng: watch("location")?.lng || 51.3347,
                    }}
                    disabledLocationPick={!enableEditing}
                    coordinateFields={enableEditing ? "editable" : "readonly"}
                    showAddress={enableEditing}
                    onAddressResolved={(addr) => {
                      // // Fill only when the field is still empty; keep existing.
                      // if (!getValues("address")) setValue("address", addr);
                    }}
                    onUseAddress={(addr) => setValue("address", addr)}
                  />
                </div>
              )}
            />
            {/*<Controller*/}
            {/*  name={"latitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("latitude")}*/}
            {/*        placeholder={t("latitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*        InputLabelProps={{*/}
            {/*          shrink: !!field.value,*/}
            {/*        }}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
            {/*<Controller*/}
            {/*  name={"longitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("longitude")}*/}
            {/*        placeholder={t("longitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*        InputLabelProps={{*/}
            {/*          shrink: !!field.value,*/}
            {/*        }}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
          </div>
          {/*<div>*/}
          {/*  <span className="text-sm text-gray-600">*/}
          {/*    {t("jurisdiction")}*/}
          {/*    {*/}
          {/*      // availableJurisdictions?.find(*/}
          {/*      //   (item) => item.id === getValues("jurisdictionId")*/}
          {/*      // )?.name*/}
          {/*      getValues("jurisdictionId")*/}
          {/*    }*/}
          {/*  </span>*/}
          {/*</div>*/}
          {/*<RegionAutoComplete*/}
          {/*  defaultValue={{*/}
          {/*    jurisdictionId: watch("jurisdictionId"),*/}
          {/*  }}*/}
          {/*  onChange={(value) => {*/}
          {/*    setValue("jurisdictionId", value?.jurisdiction?.id);*/}
          {/*  }}*/}
          {/*/>*/}

          <Controller
            name={"address"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  label={t("address")}
                  placeholder={t("address")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"w-full"}
                  multiline={true}
                  minRows={2}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
      </div>

      <div className="w-full flex items-center justify-end mt-4">
        {enableEditing ? (
          <Button
            variant="contained"
            // type={"submit"}
            onClick={() => {
              handleSubmit(onSaveClick)();
            }}
            loading={isButtonLoading}
          >
            {t("save")}
          </Button>
        ) : (
          <Permission allowed={["d_apartment", "superadmin"]}>
            <Button variant="outlined" onClick={onEditClick}>
              {t("edit")}
            </Button>
          </Permission>
        )}
      </div>
    </CollapsibleSection>
  );
};
